import React, { useMemo } from 'react'
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Divider,
    Avatar,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { format, parseISO, isValid as isValidDateFns } from 'date-fns'
import { getProjectColorTokens } from './projectCalendarColors'

const formatDateTitle = (date) => {
    if (!date) return ''
    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    }).format(date)
}

// project helpers
const getTitle = (p) => p?.title || p?.name || 'Untitled Project'

const toDateOnly = (val) => {
    if (!val) return ''

    if (val instanceof Date) {
        return isValidDateFns(val) ? format(val, 'dd/MM/yyyy') : ''
    }

    if (typeof val === 'string') {
        const iso = parseISO(val)
        if (isValidDateFns(iso)) return format(iso, 'dd/MM/yyyy')

        const d = new Date(val)
        if (!Number.isNaN(d.getTime())) return format(d, 'dd/MM/yyyy')

        return val
    }

    const d = new Date(val)
    if (!Number.isNaN(d.getTime())) return format(d, 'dd/MM/yyyy')
    return ''
}

const getRange = (p) => {
    const startRaw = p?.startDate || p?.start || p?.start_date
    const endRaw = p?.endDate || p?.end || p?.deadline || p?.end_date

    const start = toDateOnly(startRaw)
    const end = toDateOnly(endRaw)

    if (!start && !end) return ''
    if (start && !end) return start
    if (!start && end) return end
    return `${start} – ${end}`
}

const getMemberName = (m) => m?.name || m?.full_name || m?.label || ''

const getMemberInitials = (m) => {
    const explicit = m?.initials
    if (explicit) return String(explicit).slice(0, 2).toUpperCase()

    const name = getMemberName(m)
    if (!name) return '??'

    const parts = name.trim().split(/\s+/).filter(Boolean)
    const first = parts[0]?.[0] || ''
    const second = parts.length > 1 ? parts[1]?.[0] : parts[0]?.[1] || ''
    return (first + second).toUpperCase() || '??'
}

const getMemberAvatarUrl = (m) =>
    m?.staff_profile_image?.profile_image_url ??
    m?.profile_image_url ??
    m?.avatarUrl ??
    m?.profile_picture ??
    m?.profilePictureUrl ??
    m?.photo_url ??
    ''

const StackedAvatars = ({ members = [] }) => {
    if (!members?.length) return null

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {members.slice(0, 3).map((m, i) => {
                const src = getMemberAvatarUrl(m)
                return (
                    <Avatar
                        key={m?.id ?? `${getMemberName(m) || getMemberInitials(m)}-${i}`}
                        src={src || undefined}
                        imgProps={{ referrerPolicy: 'no-referrer' }}
                        alt={getMemberName(m) || getMemberInitials(m)}
                        sx={{
                            width: 32,
                            height: 32,
                            fontSize: 12,
                            border: '2px solid #fff',
                            ml: i === 0 ? 0 : -1,
                            bgcolor: 'grey.900',
                        }}
                    >
                        {!src ? getMemberInitials(m) : null}
                    </Avatar>
                )
            })}
        </Box>
    )
}

export default function TrackerCalendarModal({
    open,
    onClose,
    date,
    projects = [],
    onProjectClick,

    showMembers = false,
    members = [],
}) {
    const title = useMemo(() => `${formatDateTitle(date)} – Projects`, [date])

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: 'rgba(25,118,210,0.10)',
                            flexShrink: 0,
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 45 45" fill="none">
                            <circle cx="22.5" cy="22.5" r="22.5" fill="#DEECFF" />
                            <path
                                d="M23 24.4422H29.1111V30.4422H23V24.4422ZM31.5556 13.6422H30.3333V11.2422H27.8889V13.6422H18.1111V11.2422H15.6667V13.6422H14.4444C13.1 13.6422 12 14.7222 12 16.0422V32.8422C12 34.1622 13.1 35.2422 14.4444 35.2422H31.5556C32.9 35.2422 34 34.1622 34 32.8422V16.0422C34 14.7222 32.9 13.6422 31.5556 13.6422ZM31.5556 16.0422V18.4422H14.4444V16.0422H31.5556ZM14.4444 32.8422V20.8422H31.5556V32.8422H14.4444Z"
                                fill="#2666BE"
                            />
                        </svg>
                    </Box>

                    <Typography
                        variant="h3"
                        fontWeight={500}
                        noWrap
                        sx={{ minWidth: 0 }}
                        title={title}
                        color="#3A3A3A"
                    >
                        {title}
                    </Typography>
                </Box>

                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Divider />

            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {projects.length === 0 ? (
                        <Typography color="text.secondary">No projects on this day.</Typography>
                    ) : (
                        projects.map((p) => {
                            const colorTokens = getProjectColorTokens(p)

                            return (
                                <Box
                                    key={p?.id ?? `${getTitle(p)}-${getRange(p)}`}
                                    onClick={() => onProjectClick?.(p)}
                                    sx={{
                                        border: `1px solid ${colorTokens.accentBorder}`,
                                        borderRadius: 2.5,
                                        px: 2,
                                        py: 1.5,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            background: colorTokens.accentSurface,
                                            boxShadow: `0 14px 30px ${colorTokens.accentSoft}`,
                                            transform: 'translateY(-1px)',
                                        },
                                        transition: 'all 0.18s ease',
                                        background: colorTokens.accentSurface,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        minHeight: 72,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', gap: 1.5, minWidth: 0, flex: 1 }}>
                                        <Box
                                            sx={{
                                                width: 4,
                                                borderRadius: 999,
                                                bgcolor: colorTokens.accent,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    fontWeight: 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    color: colorTokens.accentText,
                                                }}
                                                title={getTitle(p)}
                                            >
                                                {getTitle(p)}
                                            </Typography>

                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    mt: 0.25,
                                                    fontWeight: 400,
                                                    color: 'text.secondary',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                title={getRange(p)}
                                            >
                                                {getRange(p)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {showMembers ? <StackedAvatars members={members} /> : null}
                                </Box>
                            )
                        })
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}
